import React, { useState } from 'react';
import axios from 'axios';
import {
  Card, CardContent, Typography, Box, Switch, TextField, Button, Alert, CircularProgress
} from '@mui/material';
import {
  Schedule, AccessTime, CheckCircle, Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { CardTitle, SecondaryText, SuccessButton, DangerButton, GlassmorphicCard } from '../styles/sharedStyles';

const StyledCard = styled(GlassmorphicCard)(({ theme }) => ({
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const JobIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'enabled'
})(({ enabled }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '16px',
  background: enabled
    ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(0, 212, 170, 0.1))'
    : 'linear-gradient(135deg, rgba(255, 68, 68, 0.1), rgba(255, 107, 107, 0.1))',
  border: `1px solid ${enabled ? 'rgba(0, 255, 136, 0.3)' : 'rgba(255, 68, 68, 0.3)'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  boxShadow: enabled
    ? '0 4px 16px rgba(0, 255, 136, 0.1)'
    : '0 4px 16px rgba(255, 68, 68, 0.1)',
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: '#00ff88',
      '& + .MuiSwitch-track': {
        backgroundColor: 'rgba(0, 255, 136, 0.3)',
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: 'rgba(255, 68, 68, 0.3)',
  },
}));

const TimeInput = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'success'
})(({ theme, success }) => ({
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: success ? 'rgba(0, 255, 136, 0.5)' : 'rgba(255, 255, 255, 0.1)',
      transition: 'border-color 0.3s ease',
    },
    '&:hover fieldset': {
      borderColor: success ? 'rgba(0, 255, 136, 0.7)' : 'rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: success ? '#00ff88' : '#00d4ff',
    },
  },
  '& .MuiOutlinedInput-input': {
    color: success ? '#00ff88' : '#ffffff',
    fontSize: '0.875rem',
    fontWeight: 500,
    textAlign: 'center',
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
}));

const CronJobCard = ({ job, onUpdateEnabled, onUpdateSchedule, apiBaseUrl, token }) => {
  const [enabled, setEnabled] = useState(job?.enabled || false);
  const [hour, setHour] = useState(job?.schedule?.hour || '0');
  const [minute, setMinute] = useState(job?.schedule?.minute || '0');
  const [loading, setLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Defensive check for job object - must come after hooks
  if (!job) {
    console.error('CronJobCard: job prop is null or undefined');
    return null;
  }

  const handleToggleEnabled = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${apiBaseUrl}/api/cron-jobs/${job.id}/enabled`, {
        enabled: !enabled
      }, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const newEnabled = !enabled;
      setEnabled(newEnabled);
      onUpdateEnabled(job.id, newEnabled);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error updating cron job:', err);
      setError('Failed to update job status');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSchedule = async () => {
    setScheduleLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${apiBaseUrl}/api/cron-jobs/${job.id}/schedule`, {
        schedule: {
          hour: hour.toString(),
          minute: minute.toString()
        }
      }, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      onUpdateSchedule(job.id, hour.toString(), minute.toString());
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Error updating cron schedule:', err);
      setError('Failed to update schedule');
    } finally {
      setScheduleLoading(false);
    }
  };

  const formatTime = (h, m) => {
    const hour12 = parseInt(h) % 12 || 12;
    const ampm = parseInt(h) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${m.padStart(2, '0')} ${ampm}`;
  };

  return (
    <StyledCard>
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <JobIcon enabled={enabled}>
              <Schedule sx={{
                fontSize: 24,
                color: enabled ? '#00ff88' : '#ff4444'
              }} />
            </JobIcon>
            <Box>
              <Typography variant="h6" sx={{
                fontWeight: 600,
                fontSize: '1rem',
                color: '#ffffff',
                mb: 0.5
              }}>
                {job.description || `Cron Job ${job.id}`}
              </Typography>
              <Typography variant="body2" sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '0.75rem'
              }}>
                ID: {job.id}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#00d4ff' }} />
            ) : (
              <StyledSwitch
                checked={enabled}
                onChange={handleToggleEnabled}
                size="small"
              />
            )}
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '12px',
            px: 2,
            py: 1.5,
            mb: 2
          }}>
            <AccessTime sx={{ fontSize: 18, color: '#00d4ff', mr: 1 }} />
            <Typography variant="body1" sx={{
              color: success ? '#00ff88' : '#ffffff',
              fontSize: '0.875rem',
              fontWeight: 500,
              transition: 'color 0.3s ease'
            }}>
              {formatTime(hour, minute)}
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            mb: 2
          }}>
            <TimeInput
              type="number"
              label="Hour"
              value={hour}
              onChange={(e) => setHour(e.target.value)}
              inputProps={{ min: 0, max: 23 }}
              size="small"
              success={success}
              sx={{ flex: 1 }}
            />
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mx: 0.5 }}>:</Typography>
            <TimeInput
              type="number"
              label="Minute"
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
              inputProps={{ min: 0, max: 59 }}
              size="small"
              success={success}
              sx={{ flex: 1 }}
            />
          </Box>

          <Button
            variant="outlined"
            onClick={handleUpdateSchedule}
            disabled={scheduleLoading}
            sx={{
              borderColor: success ? 'rgba(0, 255, 136, 0.5)' : 'rgba(255, 255, 255, 0.2)',
              color: success ? '#00ff88' : '#ffffff',
              '&:hover': {
                borderColor: success ? '#00ff88' : '#00d4ff',
                backgroundColor: success ? 'rgba(0, 255, 136, 0.1)' : 'rgba(0, 212, 255, 0.1)',
              },
              transition: 'all 0.3s ease',
            }}
            fullWidth
          >
            {scheduleLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: success ? '#00ff88' : '#00d4ff' }} />
                Updating...
              </>
            ) : success ? (
              <>
                <CheckCircle sx={{ mr: 1, fontSize: 16 }} />
                Updated
              </>
            ) : (
              'Update Schedule'
            )}
          </Button>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              borderRadius: '12px',
              background: 'rgba(255, 68, 68, 0.1)',
              border: '1px solid rgba(255, 68, 68, 0.3)',
              color: '#ff4444',
              '& .MuiAlert-icon': {
                color: '#ff4444',
              },
            }}
            icon={<ErrorIcon />}
          >
            {error}
          </Alert>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default CronJobCard;